import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import DetailRow, { DetailRowProps } from './CustomComponents/DetailRow';
import { TreeNodeDTO, WellHitImageRequestDTO } from 'business/models/Models';
import RecentDocumentService from 'business/services/RecentDocumentService';
import { getDateISOFormat, getTreeNodeImageProps } from 'utils/Global';
import { IStyle } from 'Styles';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import imgPrevious from 'components/main/images/Previous.png';
import imgNext from 'components/main/images/Next.png';
import ExperimentService from 'business/services/ExperimentService';
import WellCanvasInfoHeader from 'components/plateviewer/well/wellCanvasInfoHeader';
import WellService from 'business/services/WellService';
import './HitsImage.scss';
import { RMSvgIconImage } from 'components/customComponents/CommonComponents/RMSvgIcon';
import Scores from 'components/plateviewer/plate/Scores';

enum WellThumbNailImageType {
  Thumbnail_HQ = 'th',
  Thumbnail_LQ = 'th_low',
}

const _disputeIcon = RMSvgIconImage({ name: 'DisputeScoreIcon' });
const defaultStartIndex = 1;

export default function HitsImage() {
  const [hitExps, setHitExps] = useState<DetailRowProps[]>([]);
  const [selectedExperimentNodeId, setSelectedExperimentNodeId] = useState(0);
  const [experimentInfoList, setExperimentInfoList] = useState<any>([]);
  const [selectedWellNumber, setSelectedWellNumber] = useState(0);
  const [selectedPlateNumber, setSelectedPlateNumber] = useState(defaultStartIndex);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingExperimentInfo, setIsLoadingExperimentInfo] = useState<boolean>(false);
  const imageContainerRef = useRef(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const recentDocumentService = new RecentDocumentService();
        const hitImageExperiments: TreeNodeDTO[] =
          await recentDocumentService.getHitImageExperiments();
        setHitImageData(hitImageExperiments);
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function fetchExperimentInfo() {
      try {
        setIsLoadingExperimentInfo(true);
        const expInfo = await new ExperimentService().getExperimentService(
          selectedExperimentNodeId,
        );
        const expInfoList = [...experimentInfoList];
        const expPlateHitScores =
          await new ExperimentService().getExperimentPlateHitScores(
            selectedExperimentNodeId,
          );
        const curExpScoresSet = expPlateHitScores.filter(
          (obj, index, self) =>
            index ===
            self.findIndex(
              (o) => o.wellNumber === obj.wellNumber && o.plateId === obj.plateId,
            ),
        );
        for (const plate of expInfo.plateDto) {
          plate['expHitScores'] = [...expPlateHitScores].filter(
            (scr) => scr.plateId === plate.plateId,
          );
          plate['expHitsSet'] = [...curExpScoresSet].filter(
            (scr) => scr.plateId === plate.plateId,
          );
          plate['wellImages'] = [];
        }
        expInfoList.push(expInfo);
        setExperimentInfoList(expInfoList);
        setSelectedWellNumber(defaultStartIndex);
        setSelectedPlateNumber(defaultStartIndex);
      } finally {
        setIsLoadingExperimentInfo(false);
      }
    }

    const selectedExperimentInfo = getCurrentExperimentInfo(selectedExperimentNodeId);

    if (selectedExperimentNodeId && !selectedExperimentInfo) {
      fetchExperimentInfo();
    } else if (selectedExperimentInfo) {
      setSelectedWellNumber(defaultStartIndex);
      setSelectedPlateNumber(defaultStartIndex);
    }
  }, [selectedExperimentNodeId]);

  useEffect(() => {
    async function fetchCurrentWellImage(curExpPlate, curExp) {
      const wellService = new WellService();
      const index = selectedWellNumber - 1;
      const { plateId, wellNumber, scoreBatchId, dropNumber } =
        curExpPlate?.expHitsSet[index];
      const cpIdList = curExp?.profiles?.map((obj) => obj.id);
      const { regionName, regionId } = curExp?.regions[0]?.drops?.find(
        (drop) =>
          drop.wellNumber === curExpPlate?.expHitsSet[index]?.wellNumber &&
          drop.dropNumber === curExpPlate?.expHitsSet[index]?.dropNumber &&
          drop.plateId === curExpPlate?.expHitsSet[index]?.plateId,
      );

      const wellHitImageRequestDTO: WellHitImageRequestDTO = new WellHitImageRequestDTO({
        pid: plateId,
        wellNumber,
        batchId: scoreBatchId,
        dropNumber,
        cpIdList,
        regionName,
        regionId,
        width: 0,
        height: 0,
        bestFocusImageRequired: false,
        thumbnailImageType: WellThumbNailImageType.Thumbnail_HQ,
      });

      const currentWellImage = await wellService.getWellImageforHitsImageExp(
        wellHitImageRequestDTO,
      );
      const newExpInfoList = [...experimentInfoList];

      const wellImgList = newExpInfoList?.find(
        (exp) => exp.experimentId === selectedExperimentNodeId,
      )?.plateDto[selectedPlateNumber - 1]?.wellImages;
      wellImgList.push({ ...currentWellImage, plateId: plateId });
      setExperimentInfoList(newExpInfoList);
    }

    const selectedWell = getCurrentSelectedWell();
    const curExpPlate = getCurrentExperimentPlateInfo(selectedExperimentNodeId);
    if (selectedWellNumber && !selectedWell && curExpPlate?.expHitScores?.length) {
      const curExp = getCurrentExperimentInfo(selectedExperimentNodeId);
      fetchCurrentWellImage(curExpPlate, curExp);
    }
  }, [
    selectedWellNumber,
    selectedExperimentNodeId,
    experimentInfoList,
    selectedPlateNumber,
  ]);

  const getCurrentExperimentInfo = (expId) => {
    const expInfo = experimentInfoList?.find((exp) => exp.experimentId === expId);
    return expInfo;
  };

  const getCurrentExperimentPlateInfo = (expId) => {
    return getCurrentExperimentInfo(expId)?.plateDto[selectedPlateNumber - 1];
  };

  const getCurrentSelectedWell = () => {
    const currentExp = getCurrentExperimentPlateInfo(selectedExperimentNodeId);
    const well = currentExp?.wellImages?.find(
      (well) =>
        well.wellNumber === currentExp?.expHitsSet[selectedWellNumber - 1]?.wellNumber &&
        well.plateId === currentExp?.expHitsSet[selectedWellNumber - 1]?.plateId,
    );
    return well;
  };

  const setHitImageData = (hitImageTreeNodes) => {
    const expList = [];
    if (!hitImageTreeNodes) {
      return;
    }
    hitImageTreeNodes?.forEach((docs) => {
      const row: DetailRowProps = {
        text: `${docs.name}`,
        lastImaged: `Last Imaged: ${getDateISOFormat(docs.dateImaged)}`,
        startingIcon: getTreeNodeImageProps(docs),
        backGroundClr: 'white',
        fontClr: 'black',
        node: docs,
      };
      expList.push(row);
    });

    setHitExps([...expList]);
    hitImageTreeNodes[0] && onClickRow(hitImageTreeNodes[0]?.extra?.id, 0, expList);
  };

  const getRows = useMemo(() => {
    const render = [];
    hitExps.map((item, index) => {
      item?.node?.extra &&
        render.push(
          <DetailRow
            key={`recently-opened-document-${index}`}
            titleTip={item.text}
            isHitImageExp={true}
            enableDoubleClick={true}
            onClick={() => onClickRow(item?.node?.extra?.id, index, [])}
            rowClassName="recentOpenedDetailRow whitespace-pre-line"
            {...item}
          />,
        );
    });
    return render;
  }, [hitExps, experimentInfoList]);

  const onClickNext = () => {
    selectedWellNumber <
      getCurrentExperimentPlateInfo(selectedExperimentNodeId)?.expHitsSet?.length &&
      setSelectedWellNumber(selectedWellNumber + 1);
  };

  const onClickPrevious = () => {
    selectedWellNumber > 1 && setSelectedWellNumber(selectedWellNumber - 1);
  };

  const onPlateClickNext = () => {
    if (
      selectedPlateNumber <
      getCurrentExperimentInfo(selectedExperimentNodeId)?.plateDto?.length
    ) {
      setSelectedPlateNumber(selectedPlateNumber + 1);
      setSelectedWellNumber(defaultStartIndex);
    }
  };

  const onPlateClickPrevious = () => {
    if (selectedPlateNumber > 1) {
      setSelectedPlateNumber(selectedPlateNumber - 1);
      setSelectedWellNumber(defaultStartIndex);
    }
  };

  const renderNavButtons = (
    title,
    startIndex,
    endIndex,
    onClickNextValue,
    onClickPreviousValue,
  ) => {
    return (
      <div id="hitsimage-rendernavbuttons-wrapper" style={styles.navButtons}>
        <div id="hitsimage-rendernavbuttons-detail" style={styles.navButtonText}>
          {title}
        </div>
        <RMCustomButton
          onClick={onClickPreviousValue}
          contentContainerClass="navigationButton"
          contentContainerStyle={{ backgroundImage: `url(${imgPrevious})` }}
        />
        <div id="hitsimage-rendernavbuttons-pagination" style={styles.navButtonText}>{`${
          title == 'Image'
            ? `${endIndex == 0 ? 0 : startIndex} of ${endIndex}`
            : startIndex
        }`}</div>
        <RMCustomButton
          onClick={onClickNextValue}
          contentContainerClass="navigationButton"
          contentContainerStyle={{ backgroundImage: `url(${imgNext})`, marginLeft: 0 }}
        />
      </div>
    );
  };

  const showExperimentWellsInfo = useMemo(() => {
    const curExpScoresList =
      getCurrentExperimentPlateInfo(selectedExperimentNodeId)?.expHitScores || [];
    const curExpScoresSet =
      getCurrentExperimentPlateInfo(selectedExperimentNodeId)?.expHitsSet || [];
    const curExpHitScores = curExpScoresSet[selectedWellNumber - 1] || {};
    const { wellRow, wellCol, dropNumber, wellNumber, plateId } = curExpHitScores;
    const currentWellScores = curExpScoresList
      ?.filter((scr) => scr.wellNumber === wellNumber && scr.plateId === plateId)
      ?.distinctBy('scoreName');
    const scoresComponent = (
      <Scores
        scoresOfWellDrops={currentWellScores}
        width={24}
        height={24}
        x={0}
        y={0}
        scale={0}
        icon={_disputeIcon}
        wellView={true}
      />
    );
    return isLoadingExperimentInfo ? (
      <div
        id="hitsimage-show-experiment-wells-info-container"
        ref={imageContainerRef}
        style={styles.imgContainer}
        className="flex items-center justify-center"
      >
        <Spinner
          id="loading-modal-spinner-border"
          animation="border"
          className="text-[#F89723] w-8 h-8 shrink-0"
        />
      </div>
    ) : selectedExperimentNodeId ? (
      <div
        id="hitsimage-show-experiment-wells-info-container"
        ref={imageContainerRef}
        style={styles.imgContainer}
      >
        <WellCanvasInfoHeader
          canvasWidth={imageContainerRef?.current?.clientWidth}
          wellScores={scoresComponent}
          wellNumber={`${wellRow || ''}${wellCol || ''} - ${dropNumber || ''}`}
          wellInfo={''}
          disableImagingIcon={[]}
        />
        <img
          id="hitsimage-show-experiment-wells-info-img"
          src={getCurrentSelectedWell()?.wellImage}
          width="100%"
          height="77%"
        />
        <div
          id="hitsimage-show-experiment-wells-info-nav-button"
          style={styles.navButtonsBar}
        >
          {renderNavButtons(
            'Plate',
            selectedPlateNumber,
            curExpScoresSet?.length,
            onPlateClickNext,
            onPlateClickPrevious,
          )}
          {renderNavButtons(
            'Image',
            selectedWellNumber,
            curExpScoresSet?.length,
            onClickNext,
            onClickPrevious,
          )}
        </div>
      </div>
    ) : (
      <div
        id="hitsimage-show-experiment-wells-info-empty-text"
        style={styles.emptyExpScores}
      >
        Select the Experiment to show the Image
      </div>
    );
  }, [
    isLoadingExperimentInfo,
    selectedExperimentNodeId,
    selectedWellNumber,
    selectedPlateNumber,
    getCurrentSelectedWell,
    getCurrentExperimentPlateInfo,
  ]);

  const onClickRow = (treeNodeId, index, hitExpList) => {
    const expList = hitExpList.length ? hitExpList : hitExps;
    const newList = expList.map((item, i) => {
      return { ...item, backGroundClr: i === index ? '#85BAFF' : 'white' };
    });
    setHitExps(newList);
    setSelectedExperimentNodeId(treeNodeId);
  };

  return (
    <div
      id="hitsimage-wrapper"
      className="recentOpened sectionBorder"
      style={styles.mainContainer}
    >
      <div id="hitsimage-header" className="gridColumnHeader">
        Hits Image
      </div>
      {isLoading ? (
        <div
          id="hitsimage-container"
          style={styles.container}
          className="flex items-center justify-center"
        >
          <Spinner
            id="loading-modal-spinner-border"
            animation="border"
            className="text-[#F89723] w-8 h-8 shrink-0"
          />
        </div>
      ) : (
        <div id="hitsimage-container" style={styles.container}>
          <div
            id="hitsimage-recent-opened-bodies"
            className="recentOpenedBodies"
            style={{ width: '55%' }}
          >
            {getRows}
          </div>
          {showExperimentWellsInfo}
        </div>
      )}
    </div>
  );
}

const styles: IStyle = {
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    height: '85%',
    backgroundColor: '#5D5D5D',
    margin: '1%',
    borderRadius: '5px',
    padding: '0.5%',
  },
  imgContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '45%',
    backgroundColor: 'white',
  },
  navButtonsBar: {
    marginLeft: 0,
    marginRight: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    backgroundColor: 'black',
    height: '18%',
  },
  navButtons: {
    marginLeft: 0,
    marginRight: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'black',
  },
  navButtonText: {
    color: 'white',
  },
  emptyExpScores: {
    width: '45%',
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};
